import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { Breadcrumb } from '@sentry/angular';
import { environment } from 'environments/environment';

/** Bearer tokens arrive in this app's own URL (`?partnerToken=`), and Sentry attaches the page URL to every event. */
const TOKEN_QUERY_PARAMS = ['partnerToken', 'token', 'accessToken'];

export const scrubTokensFromUrl = (url: string): string =>
	url.replace(new RegExp(`([?&](?:${TOKEN_QUERY_PARAMS.join('|')})=)[^&#]*`, 'g'), '$1[redacted]');

@Injectable({
	providedIn: 'root',
})
export class SentryService {
	constructor() {
		this.initSentry();
	}

	private initSentry(): void {
		Sentry.init({
			beforeBreadcrumb(breadcrumb) {
				const data = breadcrumb.data;
				if (data) {
					['url', 'from', 'to'].forEach(key => {
						if (typeof data[key] === 'string') {
							data[key] = scrubTokensFromUrl(data[key]);
						}
					});
				}
				return breadcrumb;
			},
			dsn: environment.sentryDsn,
			environment: environment.production ? 'production' : 'development', // set environment value based on whether it's production or not
			integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
			// DO NOT CHANGE THIS VALUE, IT IS USED TO IDENTIFY THE BUILD IN SENTRY
			release: 'RELEASE_BUILD_ID',
			// Performance Monitoring
			tracesSampleRate: 0.1, // Capture 100% of the transactions, reduce in production!
			// Session Replay
			replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
			replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
			ignoreErrors: ['Non-Error exception captured'],
			beforeSend(event) {
				if (event.request?.url) {
					event.request.url = scrubTokensFromUrl(event.request.url);
				}
				return event;
			},
			// Transactions bypass `beforeSend`, and the HTTP context integration puts the page URL on them too.
			beforeSendTransaction(event) {
				if (event.request?.url) {
					event.request.url = scrubTokensFromUrl(event.request.url);
				}
				return event;
			},
		});
	}

	setUserAndOrganization(id: string, organizationId: string): void {
		this.setUserId(id);
		this.setOrganizationId(organizationId);
	}

	setUserId(id: string): void {
		Sentry.setUser({ id });
	}

	setTag(key: string, value: string): void {
		Sentry.setTag(key, value);
	}
	setOrganizationId(id: string): void {
		Sentry.setTag('organization_id', id);
	}

	captureMessage(message: string, level: Sentry.SeverityLevel = 'info'): void {
		Sentry.captureMessage(message, level);
	}

	addBreadcrumb(message: string, category = 'default', data?: Record<string, any>): void {
		const breadcrumb: Breadcrumb = {
			message: message,
			category: category,
			data: data || {},
		};
		Sentry.addBreadcrumb(breadcrumb);
	}

	setExtra(key, value) {
		Sentry.setExtra(key, value);
	}

	//adds the http response message as additional data to the Sentry error
	addHttpResponseMessage(message) {
		this.setExtra('HttpResponse', message);
	}

	//adds the component name as additional data to the Sentry error
	addComponentName(componentName: string): void {
		this.setExtra('Component', componentName);
	}

	addBankScrapingIdentifier(identifier: string): void {
		this.setExtra('Bank scraping identifier', identifier);
	}

	captureInitScrapingRequest(componentName, bankRequest) {
		this.addComponentName(componentName);
		this.addBreadcrumb('Bank request', 'scraping', bankRequest);
		this.addBankScrapingIdentifier(bankRequest.bank ?? bankRequest.bank_identifier);
		this.captureMessage('Init scraping request');
	}

	captureInitScrapingException(error, componentName, bankRequest): void {
		this.addComponentName(componentName);
		this.addBreadcrumb('Bank request', 'scraping', bankRequest);
		this.addBankScrapingIdentifier(bankRequest.bank ?? bankRequest.bank_identifier);
		this.addHttpResponseMessage(error.message);
		this.captureMessage('Initialize scraping session error', 'error');
	}

	captureEnableBankingException(componentName, params): void {
		this.addComponentName(componentName);
		this.addBreadcrumb('Enable banking params', 'scraping', params);
		this.captureMessage('Enable banking error', 'error');
	}

	capturePayoutAccountException(componentName, params): void {
		this.addComponentName(componentName);
		this.addBreadcrumb('Payout account error', 'scraping', params);
		this.captureMessage('Payout account error', 'error');
	}

	captureLegacyScrapingException(componentName, hintCode): void {
		this.addComponentName(componentName);
		this.addBreadcrumb('Bank request', 'scraping', { hintCode });
		this.captureMessage('Legacy scraping error', 'error');
	}

	captureSilentManualFallback(componentName: string, reason: 'scrapingFailed' | 'noOrgType', bankIdentifier: string): void {
		this.addComponentName(componentName);
		this.addBankScrapingIdentifier(bankIdentifier);
		this.addBreadcrumb('Silent manual fallback', 'scraping', { reason, bankIdentifier });
		this.captureMessage('Scraping silent manual fallback', 'warning');
	}

	setSelfServiceContext(params: {
		selfServiceType: 'final_payment' | 'change_rate' | 'extra_amortization' | 'pause_loan';
		organizationId: string;
		creditId: string;
		loanId?: string;
		loanIds?: string[];
	}) {
		this.setTag('self_service_type', params.selfServiceType);
		this.setTag('organization_id', params.organizationId);
		this.setExtra('credit_id', params.creditId);

		if (params.loanId) {
			this.setExtra('loan_id', params.loanId);
		}

		if (params.loanIds) {
			this.setExtra('loan_ids', params.loanIds);
		}
	}

	captureHelixHandoffSwapped(params: { partnerKey: string }) {
		this.addBreadcrumb('Helix partner token swapped', 'helix-handoff', { partner: params.partnerKey });
	}

	captureHelixHandoffException(params: { partnerKey: string; status?: number; stage: 'swap' | 'store' }) {
		const message = params.stage === 'swap' ? 'Helix partner token swap failed' : 'Helix session store failed';
		this.addBreadcrumb(message, 'helix-handoff', { partner: params.partnerKey, status: params.status });
		this.captureMessage(message, 'error');
	}
}
